import { useState } from 'react'
import { AlertTriangle, CheckCircle, Info, Maximize2, Minimize2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AttentionColumn } from './AttentionColumn'
import type { TodayBoardItem, SeveritySummary } from './useTodayBoardData'

interface AttentionBannerProps {
  items: TodayBoardItem[]
  severitySummary: SeveritySummary
  isToday: boolean
}

function SeverityBadges({ summary }: { summary: SeveritySummary }) {
  return (
    <div className="flex items-center gap-1.5">
      {summary.critical > 0 && (
        <span className="inline-flex items-center gap-0.5 rounded-full bg-red-100 dark:bg-red-950/40 px-2 py-0.5 text-[10px] text-red-700 dark:text-red-300 font-medium">
          {summary.critical} critical
        </span>
      )}
      {summary.warning > 0 && (
        <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 dark:bg-amber-950/40 px-2 py-0.5 text-[10px] text-amber-700 dark:text-amber-300 font-medium">
          {summary.warning} warning{summary.warning > 1 ? 's' : ''}
        </span>
      )}
    </div>
  )
}

export function AttentionBanner({ items, severitySummary, isToday }: AttentionBannerProps) {
  const [open, setOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'drawer' | 'modal'>('drawer')
  const count = items.length

  // Not today — show info banner
  if (!isToday) {
    return (
      <div className="flex items-center gap-2 mx-4 px-3 py-1.5 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20 text-xs text-blue-700 dark:text-blue-300">
        <Info className="h-3.5 w-3.5 shrink-0" />
        <span>Attention feed only shown for today</span>
      </div>
    )
  }

  // All clear — green banner
  if (count === 0) {
    return (
      <div className="flex items-center gap-2 mx-4 px-3 py-1.5 rounded-md border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/20 text-xs text-green-700 dark:text-green-300">
        <CheckCircle className="h-3.5 w-3.5 shrink-0" />
        <span className="font-medium">All clear</span>
      </div>
    )
  }

  const attentionContent = (
    <AttentionColumn
      items={items}
      severitySummary={severitySummary}
      isToday={isToday}
    />
  )

  const toggleButton = (
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7 shrink-0"
      onClick={() => setViewMode(viewMode === 'drawer' ? 'modal' : 'drawer')}
    >
      {viewMode === 'drawer' ? (
        <Maximize2 className="h-3.5 w-3.5" />
      ) : (
        <Minimize2 className="h-3.5 w-3.5" />
      )}
    </Button>
  )

  // Items need attention — amber banner
  return (
    <>
      {/* Banner trigger */}
      <Sheet open={open && viewMode === 'drawer'} onOpenChange={setOpen}>
        <div className="flex items-center gap-2 mx-4 px-3 py-1.5 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
          <span className="text-xs font-medium text-amber-800 dark:text-amber-200">
            {count} item{count > 1 ? 's' : ''} need{count === 1 ? 's' : ''} attention
          </span>
          <SeverityBadges summary={severitySummary} />
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-6 px-2 text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-950/40"
              onClick={() => { setViewMode('drawer'); setOpen(true) }}
            >
              View
            </Button>
          </SheetTrigger>
        </div>

        {/* Drawer view */}
        <SheetContent side="right" className="w-[540px] sm:w-[600px]">
          <SheetHeader className="flex flex-row items-center justify-between pr-2">
            <SheetTitle>Needs Attention</SheetTitle>
            {toggleButton}
          </SheetHeader>
          <div className="mt-4 h-[calc(100%-3rem)]">
            {attentionContent}
          </div>
        </SheetContent>
      </Sheet>

      {/* Modal view */}
      <Dialog open={open && viewMode === 'modal'} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex flex-row items-center justify-between pr-2 shrink-0">
            <DialogTitle>Needs Attention</DialogTitle>
            {toggleButton}
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {attentionContent}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
